import React, { useState, useMemo, useEffect } from 'react';
import { X, CheckSquare, Square } from 'lucide-react';
import { GenericColumn } from '../KanbanBoard/utils/tableColumns';

interface ColumnGroup {
  name: string;
  columns: string[];
}

interface VisibleColumns {
  [key: string]: boolean;
}

interface ColumnSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  columns: GenericColumn<any>[];
  columnGroups: ColumnGroup[];
  visibleColumns: VisibleColumns;
  setVisibleColumns: (columns: VisibleColumns) => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  isOpen,
  onClose,
  columns,
  columnGroups,
  visibleColumns,
  setVisibleColumns,
}) => {
  const [tempVisibleColumns, setTempVisibleColumns] =
    useState<VisibleColumns>(visibleColumns);

  useEffect(() => {
    if (isOpen) {
      setTempVisibleColumns(visibleColumns);
    }
  }, [isOpen, visibleColumns]);

  const toggleColumnVisibility = (columnKey: string): void => {
    setTempVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const toggleAllColumns = (value: boolean): void => {
    const newVisibleColumns: VisibleColumns = {};
    columns.forEach((column) => {
      newVisibleColumns[column.key.toString()] = value;
    });
    setTempVisibleColumns(newVisibleColumns);
  };

  const toggleGroupColumns = (group: ColumnGroup, value: boolean): void => {
    const newVisibleColumns: VisibleColumns = { ...tempVisibleColumns };
    group.columns.forEach((columnKey) => {
      newVisibleColumns[columnKey] = value;
    });
    setTempVisibleColumns(newVisibleColumns);
  };

  const isAllSelected = useMemo((): boolean => {
    return Object.values(tempVisibleColumns).every((v) => v);
  }, [tempVisibleColumns]);

  const handleApply = (): void => {
    setVisibleColumns(tempVisibleColumns);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl flex flex-col w-[32rem] max-h-[80vh]">
        {/* Header - fixed */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">
              กำหนดค่าคอลัมน์
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-[200px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="mb-3 p-2 bg-gray-50 rounded-lg sticky top-0 z-10 shadow-sm">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  toggleAllColumns(e.target.checked)
                }
                className="form-checkbox h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-gray-800 font-medium text-sm">
                เลือกทุกคอลัมน์
              </span>
            </label>
          </div>

          <div className="space-y-3">
            {columnGroups.map((group) => (
              <div
                key={group.name}
                className="bg-white border border-gray-200 rounded-lg shadow-sm"
              >
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm text-gray-700">
                      {group.name}
                    </h4>
                    <button
                      onClick={() =>
                        toggleGroupColumns(
                          group,
                          !group.columns.every((key) => tempVisibleColumns[key]),
                        )
                      }
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      {group.columns.every((key) => tempVisibleColumns[key]) ? (
                        <CheckSquare size={14} className="mr-1" />
                      ) : (
                        <Square size={14} className="mr-1" />
                      )}
                      {group.columns.every((key) => tempVisibleColumns[key])
                        ? 'ยกเลิกการเลือกทั้งหมด'
                        : 'เลือกทั้งหมด'}
                    </button>
                  </div>
                </div>
                <div className="p-2 grid grid-cols-2 gap-2">
                  {group.columns.map((columnKey) => {
                    const column = columns.find((c) => c.key.toString() === columnKey);
                    if (!column) return null;
                    return (
                      <label
                        key={column.key.toString()}
                        className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded transition-colors cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={tempVisibleColumns[column.key.toString()]}
                          onChange={() => toggleColumnVisibility(column.key.toString())}
                          className="form-checkbox h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700 truncate" title={column.label}>
                          {column.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer - fixed */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleApply}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              นำไปใช้
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnSelector;
